import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, Save, Send, AlertCircle } from 'lucide-react';
import SerialNoView from '../components/SerialNoView';
import LineItemForm from '../components/LineItemForm';
import { ErrorMessage } from '../components/ErrorMessage';
import AccountInfo from '../components/AccountInfo';
import AdditionalCharges from '../components/AdditionalCharges';
import PaymentMethods from '../components/PaymentMethods';
import FinalTaxableAmount from '../components/FinalTaxableAmount';
import VirtualDropdown from '../components/VirtualDropdown';

// Zod Validation Schema
const stockSchema = z.object({
   itemId: z.number({ required_error: 'Item ID is required' }),
   itemName: z.string({ required_error: 'Item name is required' }),
   pcs: z.number({ required_error: 'PCS is required' }),
   serialNo: z.number({ required_error: 'Serial number is required' }),
   serialNo2: z.number().optional(),
   barcode: z.number({ required_error: 'Barcode is required' }),
   isOld: z.number({ required_error: 'Is old flag is required' }),
   pinvLineDetailId: z.number().optional(),
   salesReturnLineDetailId: z.number().optional(),
});

const lineItemSchema = z.object({
   id: z.number().optional(),
   typeOfSupply: z.number({ required_error: 'Type of supply is required' }),
   itemId: z.number({ required_error: 'Item is required' }).min(1, 'Please select an item'),
   unit: z.number({ required_error: 'Unit is required' }).min(1, 'Please select an unit'),
   lineNumber: z.number().optional(),
   itemRate: z.number({ required_error: 'Item rate is required' }).min(0, 'Rate must be positive'),
   amount: z.number({ required_error: 'Amount is required' }).min(0),
   netAmount: z.number({ required_error: 'Net amount is required' }).min(0),
   ratePer: z.number({ required_error: 'Rate per is required' }),
   uomNoForRate: z.number({ required_error: 'UOM number is required' }),
   qUnitId1: z.number({ required_error: 'Quantity unit 1 is required' }),
   qUnitId2: z.number({ required_error: 'Quantity unit 2 is required' }),
   qUnitId3: z.number({ required_error: 'Quantity unit 3 is required' }),
   qty1: z.number({ required_error: 'Quantity 1 is required' }).min(0.01, 'Quantity must be greater than 0'),
   qty2: z.number({ required_error: 'Quantity 2 is required' }),
   qty3: z.number({ required_error: 'Quantity 3 is required' }),
   addLess1Percent: z.number().optional(),
   addLess1Amount: z.number().optional(),
   addLess2Percent: z.number().optional(),
   addLess2Amount: z.number().optional(),
   addLess3Percent: z.number().optional(),
   addLess3Amount: z.number().optional(),
   taxableAmount: z.number({ required_error: 'Taxable amount is required' }).min(0),
   netGst: z.number().optional(),
   gstPercent: z.number().optional(),
   cgst: z.number().optional(),
   sgst: z.number().optional(),
   igst: z.number().optional(),
   stock: z.array(stockSchema).optional(),
   hsnId: z.number({ required_error: 'HSN ID is required' }),
   sacId: z.number({ required_error: 'SAC ID is required' }),
   subHSNId: z.number({ required_error: 'Sub HSN ID is required' }),
   isDeleted: z.number({ required_error: 'Is deleted flag is required' }),
   notes: z.number({ required_error: 'Notes is required' }),
   remarks: z.number({ required_error: 'Remarks is required' }),
});

const saleBillTermSchema = z.object({
   id: z.number().optional(),
   billTermId: z.number({ required_error: 'Bill term is required' }),
   percentage: z.number({ required_error: 'Percentage is required' }),
   amount: z.number({ required_error: 'Amount is required' }),
});

const paymentSchema = z.object({
   id: z.number().optional(),
   outAdjustmentId: z.number({
      required_error: 'Out adjustment ID is required',
   }),
   daybookId: z.number({ required_error: 'Daybook ID is required' }),
   amount: z.number({ required_error: 'Amount is required' }).min(0),
});

const exchangeItemSchema = z.object({
   id: z.number().optional(),
   itemId: z.number({ required_error: 'Item ID is required' }),
   itemRate: z.number({ required_error: 'Item rate is required' }),
   isDeleted: z.number().optional(),
   purchaseId: z.number().optional(),
   salesLineDetailId: z.number().optional(),
   note1: z.number().optional(),
   isOld: z.number().optional(),
   serialNo: z.number({ required_error: 'Serial number is required' }),
   serialNo2: z.number().optional(),
   barcode: z.number().optional(),
});

const exchangeSchema = z.object({
   exchange: z.array(exchangeItemSchema).optional(),
   payments: z.array(paymentSchema).optional(),
});

const invoiceSchema = z.object({
   header: z.object({
      id: z.number().optional(),
      docno: z.string().min(1, 'Document number is required'),
      basicAmount: z.number({ required_error: 'Basic amount is required' }).min(0),
      netAmount: z.number({ required_error: 'Net amount is required' }).min(0),
      accountId: z.number({ required_error: 'Account is required' }).min(1, 'Please select an account'),
      companyId: z.number({ required_error: 'Company ID is required' }),
      daybookId: z.number({ required_error: 'Daybook ID is required' }).min(1, 'Please select an daybook'),
      incomeId: z.number().optional(),
      additionAmount: z.number().optional(),
      deductionAmount: z.number().optional(),
      dueDays: z.number({ required_error: 'Due days is required' }).min(0),
      // transactionAt: z.date({
      //   required_error: "Transaction date is required",
      //   invalid_type_error: "Invalid date format",
      // }),
      transactionAt: z.date({
         error: (issue) => {
            if (!issue.input) return 'Transaction date is required';
         },
      }),
      dueAt: z.date({
         error: (issue) => {
            if (!issue.input) return 'Due date is required';
         },
      }),
      gstAmount: z.number({ required_error: 'GST amount is required' }).min(0),
      warehouseId: z.number({ required_error: 'Warehouse is required' }),
      taxableAmount: z.number({ required_error: 'Taxable amount is required' }).min(0),
      billtoStateId: z.number({ required_error: 'Bill to state is required' }),
      shiptoStateId: z.number({ required_error: 'Ship to state is required' }),
      gstTxnType: z.number({
         required_error: 'GST transaction type is required',
      }),
      gstSubTxnType: z.number({
         required_error: 'GST sub transaction type is required',
      }),
      assesseType: z.number({ required_error: 'Assesse type is required' }),
      distance: z.number({ required_error: 'Distance is required' }).min(0),
      brokerId: z.number({ required_error: 'Broker ID is required' }),
      notes: z.string({ required_error: 'Notes is required' }),
      transportId: z.number({ required_error: 'Transport is required' }),
      vehicleId: z.number({ required_error: 'Vehicle is required' }),
      representativeId: z.number({
         required_error: 'Representative is required',
      }),
   }),
   lineItems: z.array(lineItemSchema).min(1, 'At least one line item is required'),
   saleBillTerms: z.array(saleBillTermSchema).optional(),
   payments: z.array(paymentSchema).optional(),
   exchange: exchangeSchema,
});

// Main Component
export default function SalesInvoiceForm() {
   const accounts = useMemo(() => {
      const data = [];
      for (let i = 1; i <= 50000; i++) {
         data.push({
            value: `account_${i}`,
            label:
               i === 1
                  ? '09 Central'
                  : i === 2
                  ? '09 MOBILE GALLERY CENTRAL BAZAAR'
                  : i === 3
                  ? '7 PRO TOKEN'
                  : i === 4
                  ? '8238185501'
                  : i === 5
                  ? 'AADARSHBHAI 7567564817'
                  : i === 6
                  ? 'AADESHBHAI 7976805208'
                  : `Account ${i.toString().padStart(5, '0')}`,
            mobile:
               i === 1
                  ? '7203090909'
                  : i === 4
                  ? '8238185501'
                  : i === 5
                  ? '7567564817'
                  : i === 6
                  ? '7976805208'
                  : `${Math.floor(6000000000 + Math.random() * 3999999999)}`,
            vehicle: i === 6 ? 'Vehicle' : i % 3 === 0 ? 'Vehicle' : null,
         });
      }
      return data;
   }, []);

   const {
      control,
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      formState: { errors, isSubmitting },
   } = useForm({
      resolver: zodResolver(invoiceSchema),
      defaultValues: {
         header: {
            docno: '',
            dueDays: 0,
            transactionAt: new Date(),
            dueAt: new Date(),
            basicAmount: 0,
            netAmount: 0,
            gstAmount: 0,
            taxableAmount: 0,
            distance: 0,
            notes: '',
            companyId: 1,
            daybookId: 0,
            warehouseId: 0,
            billtoStateId: 1,
            shiptoStateId: 1,
            gstTxnType: 1,
            gstSubTxnType: 1,
            assesseType: 1,
            brokerId: 0,
            transportId: 0,
            vehicleId: 0,
            representativeId: 1,
            accountId: 0,
            additionAmount: 0,
            deductionAmount: 0,
            incomeId: 0,
         },
         lineItems: [
            {
               typeOfSupply: 1,
               itemId: 0,
               unit: 0,
               itemRate: 0,
               amount: 0,
               netAmount: 0,
               ratePer: 1,
               uomNoForRate: 1,
               qUnitId1: 1,
               qUnitId2: 1,
               qUnitId3: 1,
               qty1: 0,
               qty2: 0,
               qty3: 0,
               taxableAmount: 0,
               hsnId: 1,
               sacId: 1,
               subHSNId: 1,
               isDeleted: 0,
               notes: 0,
               remarks: 0,
               addLess1Percent: 0,
               addLess1Amount: 0,
               addLess2Percent: 0,
               addLess2Amount: 0,
               addLess3Percent: 0,
               addLess3Amount: 0,
            },
         ],
         saleBillTerms: [],
         payments: [],
         exchange: {
            exchange: [],
            payments: [],
         },
      },
   });

   const { fields, append, remove } = useFieldArray({
      control,
      name: 'lineItems',
   });

   const [totals, setTotals] = useState({ subtotal: 0, gst: 0, total: 0 });

   const watchLineItems = watch('lineItems');

   useEffect(() => {
      calculateTotals();
   }, [watchLineItems]);

   const calculateTotals = () => {
      const items = watchLineItems || [];
      const subtotal = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
      const gst = items.reduce((sum, item) => sum + (Number(item.netGst) || 0), 0);
      const total = subtotal + gst;

      setTotals({ subtotal, gst, total });

      setValue('header.basicAmount', subtotal);
      setValue('header.taxableAmount', subtotal);
      setValue('header.gstAmount', gst);
      setValue('header.netAmount', total);
   };

   const calculateLineItem = (index, field, value) => {
      const qty = field === 'qty1' ? Number(value) : Number(watchLineItems[index]?.qty1 || 0);
      const rate = field === 'itemRate' ? Number(value) : Number(watchLineItems[index]?.itemRate || 0);
      const disc = field === 'addLess1Percent' ? Number(value) : Number(watchLineItems[index]?.addLess1Percent || 0);

      const amount = qty * rate;
      const discAmount = amount * (disc / 100);
      const taxableAmount = amount - discAmount;
      const gstPercent = 18; // Default GST
      const gstAmount = taxableAmount * (gstPercent / 100);
      const netAmount = taxableAmount + gstAmount;

      setValue(`lineItems.${index}.amount`, amount);
      setValue(`lineItems.${index}.taxableAmount`, taxableAmount);
      setValue(`lineItems.${index}.gstPercent`, gstPercent);
      setValue(`lineItems.${index}.netGst`, gstAmount);
      setValue(`lineItems.${index}.netAmount`, netAmount);
   };

   const addLineItem = () => {
      append({
         typeOfSupply: 1,
         itemId: 0,
         unit: 0,
         itemRate: 0,
         amount: 0,
         netAmount: 0,
         ratePer: 1,
         uomNoForRate: 1,
         qUnitId1: 1,
         qUnitId2: 1,
         qUnitId3: 1,
         qty1: 0,
         qty2: 0,
         qty3: 0,
         taxableAmount: 0,
         hsnId: 1,
         sacId: 1,
         subHSNId: 1,
         isDeleted: 0,
         notes: 0,
         remarks: 0,
         addLess1Percent: 0,
         addLess1Amount: 0,
         addLess2Percent: 0,
         addLess2Amount: 0,
         addLess3Percent: 0,
         addLess3Amount: 0,
      });
   };

   const onSubmit = async (data) => {
      try {
         console.log('Valid form data:', data);
         // Here you would send the data to your API
         alert('Form submitted successfully! Check console for data.');
         reset();
      } catch (error) {
         console.error('Submission error:', error);
      }
   };

   const onError = (errors) => {
      console.error('Validation errors:', errors);
   };

   return (
      <div className="bg-slate-950 text-slate-100 min-h-screen flex flex-col">
         {/* Top Navigation Bar */}
         <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50 flex-shrink-0">
            <div className="max-w-[1600px] mx-auto px-6 py-4">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                     <div>
                        <h1 className="text-lg font-semibold text-white">Sales Invoice</h1>
                        <p className="text-xs text-slate-400 mt-0.5">Invoice #237</p>
                     </div>
                     <div className="h-8 w-px bg-slate-700"></div>
                     <div className="flex items-center gap-2 text-xs">
                        <span className="text-slate-400">Balance:</span>
                        <span className="text-emerald-400 font-semibold">₹7,49,867.70</span>
                     </div>
                  </div>

                  <div className="flex items-center gap-2">
                     <button
                        type="button"
                        className="px-4 py-2 rounded-lg border border-slate-700 hover:bg-slate-800 transition-all text-sm font-medium flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Preview
                     </button>
                     <button
                        type="button"
                        onClick={handleSubmit(onSubmit, onError)}
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 transition-all text-sm font-medium flex items-center gap-2 text-white shadow-lg shadow-blue-500/20 disabled:opacity-50">
                        <Save className="w-4 h-4" />
                        {isSubmitting ? 'Saving...' : 'Exchange'}
                     </button>
                     <button
                        type="button"
                        onClick={handleSubmit(onSubmit, onError)}
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 transition-all text-sm font-medium flex items-center gap-2 text-white shadow-lg shadow-emerald-500/20 disabled:opacity-50">
                        <Save className="w-4 h-4" />
                        {isSubmitting ? 'Saving...' : 'Save'}
                     </button>
                  </div>
               </div>
            </div>
         </div>

         {/* Scrollable Content */}
         <div className="flex-1 overflow-y-auto">
            <form className="max-w-[1600px] mx-auto p-6">
               <div className="grid grid-cols-12 gap-6">
                  {/* Left: Main Form */}
                  <div className="col-span-12 lg:col-span-8 space-y-4">
                     {/* Invoice Details Card */}
                     <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-800 p-5 relative z-20">
                        <div className="grid grid-cols-4 gap-4">
                           {/* DAYBOOK ID  */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Daybook *</label>
                              <Controller
                                 name="account"
                                 control={control}
                                 render={({ field, fieldState: { error } }) => (
                                    <>
                                       {/* <VirtualDropdown
                                          options={accounts}
                                          value={field.value}
                                          onChange={field.onChange}
                                          placeholder="Select an account"
                                          displayKey="label"
                                          valueKey="value"
                                          error={error?.message}
                                          searchable={true}
                                          pageSize={10}
                                       /> */}
                                       <VirtualDropdown
                                          options={accounts}
                                          value={field.value}
                                          onChange={field.onChange}
                                          placeholder="Select your country..."
                                          error={errors.country?.message}
                                       />
                                       {error?.message ? (
                                          <ErrorMessage message={errors.header?.daybookId?.message} />
                                       ) : null}
                                    </>
                                 )}
                              />
                              {/* <select
                                 id="daybookId"
                                 {...register('header.daybookId', { valueAsNumber: true })}
                                 className={`w-full bg-slate-800/50 border ${
                                    errors.header?.daybookId ? 'border-red-500' : 'border-slate-700'
                                 } rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all`}>
                                 <option value={0}>Select Daybook...</option>
                                 <option value={1}>Daybook 1</option>
                                 <option value={2}>Daybook 2</option>
                              </select> */}
                              {/* <ErrorMessage message={errors.header?.daybookId?.message} /> */}
                           </div>

                           {/* DOCUMENT NUMBER  */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Document Number *</label>
                              <Controller
                                 name="header.docno"
                                 control={control}
                                 render={({ field }) => (
                                    <>
                                       <input
                                          type="text"
                                          value={field.value}
                                          onChange={(e) => field.onChange(e.target.value)}
                                          className={`w-full bg-slate-800/50 border ${
                                             errors.header?.docno ? 'border-red-500' : 'border-slate-700'
                                          } rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all`}
                                       />
                                       <ErrorMessage message={errors.header?.docno?.message} />
                                    </>
                                 )}
                              />
                           </div>

                           {/* TRANSACTION DATE  */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">
                                 Transaction Date *
                              </label>
                              <Controller
                                 name="header.transactionAt"
                                 control={control}
                                 render={({ field }) => (
                                    <>
                                       <input
                                          type="date"
                                          value={
                                             field.value instanceof Date ? field.value.toISOString().split('T')[0] : ''
                                          }
                                          onChange={(e) => field.onChange(new Date(e.target.value))}
                                          className={`w-full bg-slate-800/50 border ${
                                             errors.header?.transactionAt ? 'border-red-500' : 'border-slate-700'
                                          } rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all`}
                                       />
                                       <ErrorMessage message={errors.header?.transactionAt?.message} />
                                    </>
                                 )}
                              />
                           </div>

                           {/* ACCOUNT ID  */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Account *</label>
                              <select
                                 id="accountId"
                                 {...register('header.accountId', { valueAsNumber: true })}
                                 className={`w-full bg-slate-800/50 border ${
                                    errors.header?.accountId ? 'border-red-500' : 'border-slate-700'
                                 } rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all`}>
                                 <option value={0}>Select Account...</option>
                                 <option value={1}>Account 1</option>
                                 <option value={2}>Account 2</option>
                              </select>
                              <ErrorMessage message={errors.header?.accountId?.message} />
                           </div>

                           {/* SALESMAN ID */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Salesman</label>
                              <select
                                 id="representativeId"
                                 {...register('header.representativeId', {
                                    valueAsNumber: true,
                                 })}
                                 className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all">
                                 <option value={1}>09 Tax Admin</option>
                              </select>
                              <ErrorMessage message={errors.header?.representativeId?.message} />
                           </div>

                           {/* BROKER ID*/}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Broker</label>
                              <select
                                 id="brokerId"
                                 {...register('header.brokerId', { valueAsNumber: true })}
                                 className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all">
                                 <option value={0}>Select Broker...</option>
                              </select>
                              <ErrorMessage message={errors.header?.brokerId?.message} />
                           </div>

                           {/* INCOME ID */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Broker</label>
                              <select
                                 id="incomeId"
                                 {...register('header.incomeId', { valueAsNumber: true })}
                                 className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all">
                                 <option value={0}>Select Broker...</option>
                              </select>
                              <ErrorMessage message={errors.header?.incomeId?.message} />
                           </div>

                           {/* WAREHOUSE */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Warehouse</label>
                              <select
                                 id="warehouseId"
                                 {...register('header.warehouseId', {
                                    valueAsNumber: true,
                                 })}
                                 className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all">
                                 <option value={0}>Select Warehouse...</option>
                              </select>
                           </div>
                        </div>
                     </div>

                     {/* Items Section - Start */}

                     <LineItemForm register={register} fields={fields} addLineItem={addLineItem} errors={errors} />

                     {/* Items Section - End */}

                     <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-800 p-5">
                        <div className="grid grid-cols-4 gap-4">
                           {/* DUE DATE  */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Due Date *</label>
                              <Controller
                                 name="header.dueAt"
                                 control={control}
                                 render={({ field }) => (
                                    <>
                                       <input
                                          type="date"
                                          value={
                                             field.value instanceof Date ? field.value.toISOString().split('T')[0] : ''
                                          }
                                          onChange={(e) => field.onChange(new Date(e.target.value))}
                                          className={`w-full bg-slate-800/50 border ${
                                             errors.header?.dueAt ? 'border-red-500' : 'border-slate-700'
                                          } rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all`}
                                       />
                                       <ErrorMessage message={errors.header?.dueAt?.message} />
                                    </>
                                 )}
                              />
                           </div>

                           {/* DUE DAYS  */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Due Days *</label>
                              <input
                                 type="number"
                                 {...register('header.dueDays', { valueAsNumber: true })}
                                 className={`w-full bg-slate-800/50 border ${
                                    errors.header?.dueDays ? 'border-red-500' : 'border-slate-700'
                                 } rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all`}
                              />
                              <ErrorMessage message={errors.header?.dueDays?.message} />
                           </div>

                           {/* TRANSPORT ID  */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Transport</label>
                              <select
                                 {...register('header.transportId', {
                                    valueAsNumber: true,
                                 })}
                                 className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all">
                                 <option value={0}>Select...</option>
                              </select>
                           </div>

                           {/* VEHICLE ID  */}
                           <div>
                              <label className="text-xs font-medium text-slate-400 mb-2 block">Vehicle</label>
                              <select
                                 {...register('header.vehicleId', { valueAsNumber: true })}
                                 className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 transition-all">
                                 <option value={0}>Select...</option>
                              </select>
                           </div>
                        </div>
                        <div className="mt-4">
                           <label className="text-xs font-medium text-slate-400 mb-2 block">Notes</label>
                           <textarea
                              {...register('header.notes')}
                              rows="2"
                              placeholder="Add any notes or special instructions..."
                              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"></textarea>
                        </div>
                     </div>

                     {/* Items Serial No. Section */}
                     <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-800">
                        <div className="p-4 flex items-center justify-between">
                           <div>
                              <h2 className="text-sm font-semibold text-white">Added Serial No.</h2>
                           </div>
                        </div>

                        <SerialNoView />
                     </div>
                  </div>

                  {/* Right Sidebar */}
                  <div className="col-span-12 lg:col-span-4 space-y-4">
                     {/* Account Info */}
                     <AccountInfo />

                     {/* Additional Charges */}
                     <AdditionalCharges register={register} />

                     {/* Payment Methods */}
                     <PaymentMethods />

                     {/* Final Summary */}
                     <FinalTaxableAmount totals={totals} />

                     {/* Validation Summary */}
                     {Object.keys(errors).length > 0 && (
                        <div className="bg-red-500/10 backdrop-blur-sm rounded-xl border border-red-500/20 p-4">
                           <div className="flex items-start gap-3">
                              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                 <p className="text-xs text-red-300 font-medium mb-1">Validation Errors</p>
                                 <p className="text-xs text-slate-400 leading-relaxed">
                                    Please fix {Object.keys(errors).length} error
                                    {Object.keys(errors).length > 1 ? 's' : ''} before submitting the form.
                                 </p>
                              </div>
                           </div>
                        </div>
                     )}

                     {/* Quick Info */}
                     {/* <div className="bg-blue-500/10 backdrop-blur-sm rounded-xl border border-blue-500/20 p-4">
                           <div className="flex items-start gap-3">
                              <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                              <div>
                                 <p className="text-xs text-blue-300 font-medium mb-1">Pro Tip</p>
                                 <p className="text-xs text-slate-400 leading-relaxed">
                                    Required fields are marked with *. All calculations are done in real-time!
                                 </p>
                              </div>
                           </div>
                        </div> */}
                  </div>
               </div>
            </form>
         </div>
      </div>
   );
}
